import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import { snapshotTranslations } from '~/assets/control-panel-translations'

/**
 * Translation control panel state.
 */
export const useControlPanelStore = defineStore('control-panel', () => {
  const cacheStore = useCacheStore()
  const cmsFetch = useCmsFetch()
  const { locale } = useI18n()

  const enabled = ref(false)
  /*
   * Raw translations map; the `translations` getter exposes it filtered + sorted, so this needs a
   * different name - Pinia shares one namespace for state and getters.
   */
  const translationsMap = ref<Record<string, string>>({})
  const translationUrls = ref<Record<string, string>>({})

  const isEnabled = computed(() => enabled.value)
  const translations = computed(() =>
    Object.entries(translationsMap.value)
      .filter(([key]) => !key.startsWith('control-panel.'))
      .sort(([a], [b]) => (a < b ? -1 : 1)),
  )

  function setTranslationUrls(newTranslationUrls: Record<string, string>) {
    translationUrls.value = newTranslationUrls
  }
  function syncTranslations() {
    // Pull the translations collected by the `postTranslation` hook (assets/control-panel-translations.ts)
    // into reactive state. Called after render (mount / route change) so the reactive write cannot loop
    // with the panel that reads this list.
    translationsMap.value = snapshotTranslations()
  }
  function clearTranslations() {
    translationsMap.value = {}
  }
  function reset() {
    enabled.value = false
  }
  function toggle() {
    enabled.value = !enabled.value
  }
  function enable() {
    enabled.value = true
  }

  async function fetchTranslationUrls() {
    // Early return when the control-panel is disabled
    if (!isEnabled.value) {
      return
    }

    const key = `control-panel;translationUrls;locale:${locale.value}`
    const ttl = 300

    const fetcher = async () => {
      return (await cmsFetch(`/api/${locale.value}/translation-urls.json`)).data
    }

    setTranslationUrls(await cacheStore.fetch({ key, ttl, fetcher }))
  }

  return {
    enabled,
    translationsMap,
    translationUrls,
    isEnabled,
    translations,
    setTranslationUrls,
    syncTranslations,
    clearTranslations,
    reset,
    toggle,
    enable,
    fetchTranslationUrls,
  }
})
